import {
  Contact,
  ContactList,
  ContactsApi,
  ErrorResponse,
  FindContactsRequest,
} from '@citruscamps/citrus-client'
import { Dispatch } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query'
import {
  DefaultDetailsPagination,
  DefaultMainPagination,
} from '../../../../../constants/pagination'
import {
  IFetchManyProps,
  NonQueryAttributes,
  useFetchInfiniteMany,
  useFetchMany,
} from '../../../../../hooks/useFetchMany'
import { Pagination } from '../../../../../interfaces/pagination'
import { generateApiConfig } from '../../../../../utils/client-config'
import { useUpdateContacts } from './useUpdateContacts'

export const DefaultContactFetchProps: ContactFetchProps = {
  sort: 'first_name',
  order: 'ASC',
  query: {
    is_archived: undefined,
    has_account: undefined,
  },
}

export type ContactFilterProps = Omit<FindContactsRequest, NonQueryAttributes>

export interface ContactFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  query: ContactFilterProps
}

type IProps = Pick<
  IFetchManyProps<Contact, ContactFilterProps>,
  'programId' | 'fetchProps' | 'pagination' | 'setFetchProps'
>

interface FetchContacts {
  data: Contact[]
  error?: ErrorResponse
  fetchProps: ContactFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  handleEmail: (values: Contact) => void
  handleTextMessage: (values: Contact) => void
  handleCall: (values: Contact) => void
  handleUpdate: (values: Contact) => Promise<void>
  handleArchive: (values: Contact) => Promise<void>
  handleInvite: (values: Contact) => Promise<void>
  handleUnarchive: (values: Contact) => Promise<void>
  handleSubscribe: (values: Contact) => Promise<void>
  handleUnsubscribe: (values: Contact) => Promise<void>
  setFetchProps: (value?: ContactFetchProps) => void
  setPagination: Dispatch<Pagination>
  setSearch: Dispatch<string>
}

export const useFetchContacts = (props: IProps): FetchContacts => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setPagination,
    setSearch,
    setFetchProps,
  } = useFetchMany<Contact, ContactFilterProps>({
    type: 'contact',
    programId: props.programId,
    fetchProps: props.fetchProps,
    defaultFetchProps: DefaultContactFetchProps,
    defaultPagination: props.pagination || DefaultMainPagination,
    setFetchProps: props.setFetchProps,
    queryFn: async (queryParams): Promise<ContactList> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new ContactsApi(generateApiConfig())
      return await client.findContacts(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search,
          email: query?.email || undefined,
          has_account: typeof query?.has_account !== 'undefined' ? query?.has_account : undefined,
          is_archived: typeof query?.is_archived !== 'undefined' ? query?.is_archived : undefined,
          is_subscribed:
            typeof query?.is_subscribed !== 'undefined' ? query?.is_subscribed : undefined,
        },
        { signal },
      )
    },
  })
  const {
    handleEmail,
    handleTextMessage,
    handleCall,
    handleArchive,
    handleInvite,
    handleUpdate,
    handleUnarchive,
    handleSubscribe,
    handleUnsubscribe,
  } = useUpdateContacts({ programId: props.programId })

  return {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    handleEmail,
    handleTextMessage,
    handleCall,
    handleUpdate,
    handleArchive,
    handleInvite,
    handleUnarchive,
    setFetchProps,
    setPagination,
    setSearch,
    handleSubscribe,
    handleUnsubscribe,
  }
}

type IInfiniteProps = Omit<IProps, 'isFilterPersisted'>

interface FetchInfiniteContacts extends Omit<FetchContacts, 'setFetchProps' | 'setPagination'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<ContactList, ErrorResponse>>
}

export const useFetchInfiniteContacts = (props: IInfiniteProps): FetchInfiniteContacts => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setSearch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useFetchInfiniteMany<Contact, ContactFilterProps>({
    type: 'contact',
    programId: props.programId,
    fetchProps: props.fetchProps || DefaultContactFetchProps,
    pagination: props.pagination || DefaultDetailsPagination,
    queryFn: async (queryParams): Promise<ContactList> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new ContactsApi(generateApiConfig())
      return await client.findContacts(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          search: search || undefined,
          email: query?.email || undefined,
          has_account: typeof query?.has_account !== 'undefined' ? query?.has_account : undefined,
          is_archived: typeof query?.is_archived !== 'undefined' ? query?.is_archived : undefined,
          is_subscribed:
            typeof query?.is_subscribed !== 'undefined' ? query?.is_subscribed : undefined,
        },
        { signal },
      )
    },
  })
  const {
    handleEmail,
    handleTextMessage,
    handleCall,
    handleUpdate,
    handleArchive,
    handleInvite,
    handleUnarchive,
    handleSubscribe,
    handleUnsubscribe,
  } = useUpdateContacts({ programId: props.programId })

  return {
    data,
    error,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    pagination,
    search: _search,
    fetchNextPage,
    handleEmail,
    handleTextMessage,
    handleCall,
    handleUpdate,
    handleArchive,
    handleInvite,
    handleUnarchive,
    handleSubscribe,
    handleUnsubscribe,
    setSearch,
  }
}
